import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Sl1Anomaly, Sl1Finding } from "shared/sl1Import"
import { Sl1AnomalyCategoryList } from "shared/sl1ImportData"
import {
  eyebrowSx,
  fmtNum,
  panelSx,
  Sl1Pill,
  sl1ResolutionTone,
  sl1SeverityColor,
} from "./Sl1ImportChrome"

/**
 * The anomalies table and the flagged-row list (LOCKER-382 §D-4.3).
 *
 * ## The severity stripe, and why it is the only strong colour here
 *
 * Severity rides on a solid left stripe on the ROW. The category, the resolution
 * and the counts are low-saturation outlined pills. That split is deliberate: a
 * report can carry seven anomaly categories across a few hundred flagged rows, and
 * if each one is a saturated chip the table becomes a paint chart in which the
 * numbers — the thing the operator came for — are the least visible element. One
 * strong signal per row, everything else quiet.
 *
 * Nothing is yellow or amber (`no-harsh-yellow-ui`). `WarningAmberIcon` appears
 * as a glyph on blocked rows only; every fill comes from the palette.
 */

export const Sl1AnomalyTable: FC<{ anomalies: Sl1Anomaly[] }> = ({
  anomalies,
}) => {
  const { t } = useTranslation()
  const present = anomalies.filter((a) => a.rows > 0)

  if (!present.length) {
    return (
      <Box sx={{ ...panelSx, p: 2.5 }}>
        <Typography sx={eyebrowSx}>{t("sl1Anomalies")}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
          {t("sl1NoAnomalies")}
        </Typography>
      </Box>
    )
  }

  return (
    <Box data-ui="UI-SL2W-177" sx={{ ...panelSx, overflow: "hidden" }}>
      <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Typography sx={eyebrowSx}>{t("sl1Anomalies")}</Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={eyebrowSx}>{t("sl1ColCategory")}</TableCell>
            <TableCell sx={eyebrowSx} align="right">
              {t("sl1ColRows")}
            </TableCell>
            <TableCell sx={eyebrowSx}>{t("sl1ColWhat")}</TableCell>
            <TableCell sx={eyebrowSx}>{t("sl1ColAction")}</TableCell>
            <TableCell sx={eyebrowSx}>{t("sl1ColSample")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {present.map((a) => (
            <TableRow
              key={a.category}
              sx={{
                borderLeft: "3px solid",
                borderLeftColor: sl1SeverityColor(a.severity),
              }}
            >
              <TableCell sx={{ minWidth: 200 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  {a.severity === "blocked" && (
                    <WarningAmberIcon sx={{ fontSize: 15, color: "error.main" }} />
                  )}
                  <Typography sx={{ fontSize: "0.78rem", fontWeight: 600 }}>
                    {t(`sl1Anomaly_${a.category}`)}
                  </Typography>
                </Stack>
                <Sl1Pill
                  label={t(`sl1Severity_${a.severity}`)}
                  tone={a.severity === "blocked" ? "error" : "info"}
                />
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
              >
                {fmtNum(a.rows)}
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "text.disabled" }}
                >
                  {a.evidence}
                </Typography>
              </TableCell>
              <TableCell sx={{ fontSize: "0.76rem", maxWidth: "34ch" }}>
                {a.what}
              </TableCell>
              <TableCell
                sx={{ fontSize: "0.74rem", color: "text.secondary", maxWidth: "46ch" }}
              >
                {a.action}
              </TableCell>
              <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}>
                {(a.sample ?? []).join(", ") || "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

/**
 * The flagged rows themselves.
 *
 * Only rows carrying an anomaly are listed. A report can hold thousands of clean
 * findings and listing them would bury the handful that need a decision — the
 * clean ones are already counted in the bar above, which is what a count is for.
 */
export const Sl1FlaggedRows: FC<{ findings: Sl1Finding[]; limit?: number }> = ({
  findings,
  limit = 200,
}) => {
  const { t } = useTranslation()
  const flagged = findings.filter((f) => f.anomalies.length > 0)
  const shown = flagged.slice(0, limit)

  return (
    <Box sx={{ ...panelSx, overflow: "hidden" }}>
      <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Typography sx={eyebrowSx}>{t("sl1FlaggedRows")}</Typography>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {t("sl1FlaggedRowsHint", {
            shown: fmtNum(shown.length),
            total: fmtNum(flagged.length),
          })}
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={eyebrowSx}>{t("sl1ColOrder")}</TableCell>
            <TableCell sx={eyebrowSx}>{t("sl1ColSstReservation")}</TableCell>
            <TableCell sx={eyebrowSx}>{t("sl1ColCreatedAt")}</TableCell>
            <TableCell sx={eyebrowSx}>{t("sl1ColLockerDoor")}</TableCell>
            <TableCell sx={eyebrowSx}>{t("sl1ColResolution")}</TableCell>
            <TableCell sx={eyebrowSx}>{t("sl1ColFlags")}</TableCell>
            <TableCell sx={eyebrowSx}>{t("sl1ColMatchedSl2")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map((f) => {
            const blocked = f.resolution === "blocked"
            return (
              <TableRow
                key={f.orderId}
                sx={{
                  borderLeft: "3px solid",
                  borderLeftColor: blocked ? "error.main" : "info.main",
                }}
              >
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.74rem" }}>
                  {f.orderId}
                </TableCell>
                {/* The MATCH KEY (N5), with the integration order id beneath it as
                    context only (N4) — it is not the key, and showing it as the
                    primary identifier is what the first cut did. */}
                <TableCell sx={{ fontSize: "0.72rem" }}>
                  <Box sx={{ fontFamily: "monospace" }}>
                    {f.sstReservationId ?? "—"}
                  </Box>
                  {f.integrationOrderId && (
                    <Box
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.66rem",
                        color: "text.secondary",
                      }}
                    >
                      {t("sl1ColExternalId")}: {f.integrationOrderId}
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}>
                  {/* The date the whole ticket exists to preserve. Shown verbatim,
                      never reformatted into a locale string. */}
                  {f.sl1CreatedAt || "—"}
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}>
                  {f.lockerRef ?? "—"} / {f.doorRef ?? "—"}
                </TableCell>
                <TableCell>
                  <Sl1Pill
                    label={t(`sl1Resolution_${f.resolution}`)}
                    tone={sl1ResolutionTone(f.resolution)}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.5}>
                    {f.anomalies.map((a) => (
                      <Sl1Pill
                        key={a}
                        label={t(`sl1Anomaly_${a}`)}
                        tone={
                          Sl1AnomalyCategoryList.includes(a) &&
                          f.resolution === "blocked"
                            ? "error"
                            : "info"
                        }
                      />
                    ))}
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}>
                  {f.matchedSl2ReservationId ??
                    (f.candidateSl2Ids?.length
                      ? t("sl1NCandidates", { n: f.candidateSl2Ids.length })
                      : "—")}
                  {/* A matched HUSK is a row the apply REPLACES rather than adds
                      (N5). The count of these is the difference between a clean
                      import and 24,742 estate-wide duplicates, so it is on the
                      row itself. */}
                  {f.matchedHusk && (
                    <Box sx={{ mt: 0.5 }}>
                      <Sl1Pill label={t("sl1HuskBadge")} tone="info" />
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            )
          })}
          {!shown.length && (
            <TableRow>
              <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {t("sl1NoFlaggedRows")}
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  )
}
